/**
 * Capacitor Deep Link Handler for OAuth
 *
 * Listens for appUrlOpen events on Android/iOS and processes
 * cloud OAuth callback URLs.
 *
 * Usage: Call `initOAuthDeepLinks()` in App.vue's onMounted.
 */

import { isCapacitor } from '../config/runtime';
import { useAuthStore } from '../stores/auth';
import { useRouter } from 'vue-router';

let initialized = false;

/**
 * Initialize OAuth deep link handling for Capacitor (Android/iOS).
 * Should be called once in App.vue's onMounted.
 */
export async function initOAuthDeepLinks(): Promise<void> {
  if (!isCapacitor() || initialized) return;
  initialized = true;

  try {
    const { App: CapacitorApp } = await import('@capacitor/app');

    CapacitorApp.addListener('appUrlOpen', async (data) => {
      const url = data.url;
      console.log('[Capacitor OAuth] Deep link received:', url);

      // Only handle OAuth callback URLs
      if (!url.includes('/oauth/callback')) {
        return;
      }

      try {
        const authStore = useAuthStore();
        const router = useRouter();

        const result = await authStore.loginToCloudViaOAuth(url);

        if (result.success) {
          console.log('[Capacitor OAuth] Login successful, navigating to /home');
          await router.push('/home');
        } else {
          console.error('[Capacitor OAuth] Login failed:', result.error);
        }
      } catch (err) {
        console.error('[Capacitor OAuth] Error processing callback:', err);
      }
    });

    console.log('[Capacitor OAuth] Deep link listener initialized');
  } catch (err) {
    console.warn('[Capacitor OAuth] Failed to initialize deep link listener:', err);
  }
}
